import { useState, useMemo, useCallback, RefObject } from 'react';
import { createPortal } from 'react-dom';
import {
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import EditIcon from '@material-ui/icons/EditOutlined';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import ClearIcon from '@material-ui/icons/Clear';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import {
  useRoleMappingPermissions,
  ForbiddenState,
} from '@openchoreo/backstage-plugin-react';
import {
  useNamespaceRoleBindings,
  useNamespaceRoles,
  useClusterRoles,
  NamespaceRoleBinding,
  NamespaceRoleBindingRequest,
} from '../hooks';
import { ClusterRoleBindingRequest } from '../../../api/OpenChoreoClientApi';
import { useNotification } from '../../../hooks';
import { useQueryParams } from '../../../hooks/useQueryParams';
import { isForbiddenError } from '../../../utils/errorUtils';
import { NotificationBanner } from '../../Environments/components';
import { CHOREO_LABELS } from '@openchoreo/backstage-plugin-common';
import { SCOPE_NAMESPACE } from '../constants';
import { BindingWizardPage } from './BindingWizardPage';
import { BindingDetailDialog, BindingDetail } from './BindingDetailDialog';

const useStyles = makeStyles(theme => ({
  filters: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  searchField: {
    width: theme.spacing(40),
  },
  filterSelect: {
    minWidth: 150,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  entitlementCell: {
    maxWidth: 200,
  },
  effectChip: {
    fontWeight: 600,
  },
  allowChip: {
    borderColor: theme.palette.success.main,
    color: theme.palette.success.main,
  },
  denyChip: {
    borderColor: theme.palette.error.main,
    color: theme.palette.error.main,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  deleteIconButton: {
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.error.main,
      backgroundColor: 'rgba(244, 67, 54, 0.08)',
    },
  },
  deleteButton: {
    borderColor: theme.palette.error.main,
    color: theme.palette.error.main,
    '&:hover': {
      borderColor: theme.palette.error.dark,
      backgroundColor: 'rgba(211, 47, 47, 0.04)',
    },
  },
}));

/** Format scope for a single namespace role mapping */
const formatNsMappingScope = (
  scope?: { project?: string; component?: string },
  namespace?: string,
): string => {
  const ns = namespace || '*';
  const parts: string[] = [`ns:${ns}`];
  if (scope?.project) {
    parts.push(`proj:${scope.project}`);
    if (scope?.component) {
      parts.push(`comp:${scope.component}`);
    } else {
      parts.push('*');
    }
  } else {
    parts.push('*');
  }
  return parts.join('/');
};

interface NamespaceRoleBindingsContentProps {
  selectedNamespace: string;
  actionsContainerRef: RefObject<HTMLDivElement>;
  wizardAction: '' | 'create' | 'edit';
  wizardBindingName: string;
}

export const NamespaceRoleBindingsContent = ({
  selectedNamespace,
  actionsContainerRef,
  wizardAction,
  wizardBindingName,
}: NamespaceRoleBindingsContentProps) => {
  const classes = useStyles();
  const notification = useNotification();
  const {
    canView,
    canCreate,
    canUpdate,
    canDelete,
    loading: permissionsLoading,
    createDeniedTooltip,
    updateDeniedTooltip,
    deleteDeniedTooltip,
  } = useRoleMappingPermissions();
  const {
    bindings,
    loading,
    error,
    fetchBindings,
    addBinding,
    updateBinding,
    deleteBinding,
  } = useNamespaceRoleBindings(selectedNamespace || undefined);

  // Get both namespace roles and cluster roles for binding
  const { roles: namespaceRoles, loading: namespaceRolesLoading } =
    useNamespaceRoles(selectedNamespace || undefined);
  const { roles: clusterRoles, loading: clusterRolesLoading } =
    useClusterRoles();

  // Combine roles for the dialog - namespace bindings can reference both.
  // Cluster roles have no namespace field; namespace roles already carry it from the API.
  const availableRoles = useMemo(
    () => [...clusterRoles, ...namespaceRoles],
    [clusterRoles, namespaceRoles],
  );

  const [, setParams] = useQueryParams({
    action: { defaultValue: '' },
    bindingName: { defaultValue: '' },
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [effectFilter, setEffectFilter] = useState<string>('all');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [bindingToDelete, setBindingToDelete] =
    useState<NamespaceRoleBinding | null>(null);
  const [detailBinding, setDetailBinding] = useState<BindingDetail | null>(
    null,
  );

  const editingBinding = useMemo(() => {
    if (wizardAction !== 'edit' || !wizardBindingName) return undefined;
    return bindings.find(b => b.name === wizardBindingName);
  }, [wizardAction, wizardBindingName, bindings]);

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setRoleFilter('all');
    setSearchQuery('');
    setEffectFilter('all');
  }, []);

  // Client-side filtering for role, effect, and search
  const filteredBindings = useMemo(() => {
    return bindings.filter(binding => {
      // Role filter
      if (roleFilter !== 'all') {
        const hasMatchingRole = (binding.roleMappings || []).some(rm => {
          const bindingKey = `${rm.role.name}|${rm.role.namespace || ''}`;
          return bindingKey === roleFilter;
        });
        if (!hasMatchingRole) {
          return false;
        }
      }

      // Effect filter
      if (effectFilter !== 'all' && binding.effect !== effectFilter) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const roleNames = (binding.roleMappings || []).map(rm =>
          rm.role.name.toLowerCase(),
        );
        const searchFields = [
          binding.name,
          ...roleNames,
          binding.entitlement.claim,
          binding.entitlement.value,
        ].map(s => s.toLowerCase());

        if (!searchFields.some(field => field.includes(query))) {
          return false;
        }
      }

      return true;
    });
  }, [bindings, roleFilter, searchQuery, effectFilter]);

  const handleRowClick = (binding: NamespaceRoleBinding) => {
    setDetailBinding({
      name: binding.name,
      entitlement: binding.entitlement,
      effect: binding.effect,
      labels: binding.labels,
      roleMappings: (binding.roleMappings || []).map(rm => ({
        role: rm.role?.name || '\u2014',
        scope: formatNsMappingScope(rm.scope, binding.namespace),
        isClusterRole: !rm.role?.namespace,
        conditions: rm.conditions,
      })),
    });
  };

  const handleCreateBinding = () => {
    setParams({ action: 'create', bindingName: '' });
  };

  const handleEditBinding = (binding: NamespaceRoleBinding) => {
    setParams({ action: 'edit', bindingName: binding.name });
  };

  const handleDeleteBinding = (binding: NamespaceRoleBinding) => {
    setBindingToDelete(binding);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteBinding = async () => {
    if (!bindingToDelete) return;

    try {
      await deleteBinding(bindingToDelete.name);
      notification.showSuccess(
        `Namespace role binding "${bindingToDelete.name}" deleted successfully`,
      );
      setDeleteConfirmOpen(false);
      setBindingToDelete(null);
    } catch (err) {
      if (isForbiddenError(err)) {
        notification.showError(
          'You do not have permission to delete this binding. Contact your administrator.',
        );
      } else {
        notification.showError(
          `Failed to delete binding: ${
            err instanceof Error ? err.message : 'Unknown error'
          }`,
        );
      }
    }
  };

  const handleSaveBinding = async (
    binding: ClusterRoleBindingRequest | NamespaceRoleBindingRequest,
  ) => {
    const nb = binding as NamespaceRoleBindingRequest;
    if (editingBinding) {
      await updateBinding(editingBinding.name, nb);
      notification.showSuccess(
        `Namespace role binding "${editingBinding.name}" updated successfully`,
      );
    } else {
      await addBinding(nb);
      notification.showSuccess(
        `Namespace role binding "${nb.name}" created successfully`,
      );
    }
    setParams({ action: '', bindingName: '' });
  };

  const handleWizardCancel = () => {
    setParams({ action: '', bindingName: '' });
  };

  const hasActiveFilters =
    roleFilter !== 'all' || searchQuery || effectFilter !== 'all';

  if (permissionsLoading) {
    return <Progress />;
  }

  if (!canView) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant="body1" color="textSecondary">
          You do not have permission to view namespace role bindings.
        </Typography>
      </Box>
    );
  }

  // View-swap: render wizard in place of the bindings list when ?action=create|edit.
  // Requires a namespace to be selected; otherwise fall through to the empty-state UI.
  if (
    selectedNamespace &&
    (wizardAction === 'create' || (wizardAction === 'edit' && editingBinding))
  ) {
    return (
      <Box>
        <NotificationBanner notification={notification.notification} />
        <BindingWizardPage
          bindingType={SCOPE_NAMESPACE}
          editingBinding={editingBinding}
          availableRoles={availableRoles}
          namespace={selectedNamespace}
          onSave={handleSaveBinding}
          onCancel={handleWizardCancel}
        />
      </Box>
    );
  }

  const actionButtons = (
    <>
      <IconButton
        onClick={() => fetchBindings()}
        size="small"
        title="Refresh"
        disabled={!selectedNamespace}
      >
        <RefreshIcon />
      </IconButton>
      <Tooltip
        title={
          !selectedNamespace ? 'Select a namespace first' : createDeniedTooltip
        }
      >
        <span>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleCreateBinding}
            disabled={!canCreate || !selectedNamespace}
          >
            New Namespace Role Binding
          </Button>
        </span>
      </Tooltip>
    </>
  );

  return (
    <Box>
      <NotificationBanner notification={notification.notification} />
      {actionsContainerRef.current &&
        createPortal(actionButtons, actionsContainerRef.current)}

      {!selectedNamespace ? (
        <Box className={classes.emptyState}>
          <Typography variant="body1" color="textSecondary">
            Select a namespace to view its role bindings.
          </Typography>
        </Box>
      ) : (
        <>
          {(loading || namespaceRolesLoading || clusterRolesLoading) && (
            <Progress />
          )}
          {!loading &&
            !namespaceRolesLoading &&
            !clusterRolesLoading &&
            error &&
            (isForbiddenError(error) ? (
              <ForbiddenState
                message="You do not have permission to view namespace role bindings."
                onRetry={() => fetchBindings()}
              />
            ) : (
              <ResponseErrorPanel error={error} />
            ))}
          {!loading &&
            !namespaceRolesLoading &&
            !clusterRolesLoading &&
            !error && (
              <>
                <Box className={classes.filters}>
                  <TextField
                    className={classes.searchField}
                    placeholder="Search..."
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <FormControl
                    variant="outlined"
                    size="small"
                    className={classes.filterSelect}
                  >
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={roleFilter}
                      onChange={e => setRoleFilter(e.target.value as string)}
                      label="Role"
                    >
                      <MenuItem value="all">All Roles</MenuItem>
                      {availableRoles.map(role => (
                        <MenuItem
                          key={`${role.name}|${role.namespace || ''}`}
                          value={`${role.name}|${role.namespace || ''}`}
                        >
                          {role.name}{' '}
                          {role.namespace ? '(Namespace)' : '(Cluster)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl
                    variant="outlined"
                    size="small"
                    className={classes.filterSelect}
                  >
                    <InputLabel>Effect</InputLabel>
                    <Select
                      value={effectFilter}
                      onChange={e => setEffectFilter(e.target.value as string)}
                      label="Effect"
                    >
                      <MenuItem value="all">All Effects</MenuItem>
                      <MenuItem value="allow">Allow</MenuItem>
                      <MenuItem value="deny">Deny</MenuItem>
                    </Select>
                  </FormControl>

                  {hasActiveFilters && (
                    <Button
                      size="small"
                      startIcon={<ClearIcon />}
                      onClick={handleClearFilters}
                    >
                      Clear Filters
                    </Button>
                  )}
                </Box>

                {filteredBindings.length === 0 ? (
                  <Box className={classes.emptyState}>
                    <Typography variant="body1" color="textSecondary">
                      {hasActiveFilters
                        ? 'No namespace role bindings match your filters'
                        : 'No namespace role bindings defined yet. Create your first binding to grant permissions.'}
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer
                    component={Paper}
                    className={classes.tableContainer}
                  >
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Binding Name</TableCell>
                          <TableCell>Role Mappings</TableCell>
                          <TableCell className={classes.entitlementCell}>
                            Entitlement (claim=value)
                          </TableCell>
                          <TableCell>Effect</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredBindings.map(binding => {
                          return (
                            <TableRow
                              key={binding.name}
                              className={classes.clickableRow}
                              onClick={() => handleRowClick(binding)}
                            >
                              <TableCell>
                                <Typography variant="body2">
                                  {binding.name}
                                  {binding.labels?.[CHOREO_LABELS.SYSTEM] ===
                                    'true' && (
                                    <Chip
                                      label="System"
                                      size="small"
                                      variant="outlined"
                                      style={{
                                        marginLeft: 8,
                                        fontSize: '0.7rem',
                                        height: 20,
                                      }}
                                    />
                                  )}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {(binding.roleMappings || []).length > 0 ? (
                                  <>
                                    {binding.roleMappings
                                      .slice(0, 2)
                                      .map((rm, idx) => (
                                        <Typography variant="body2" key={idx}>
                                          {rm.role?.name || '\u2014'} →{' '}
                                          {formatNsMappingScope(
                                            rm.scope,
                                            binding.namespace,
                                          )}
                                          {idx === 1 &&
                                            binding.roleMappings.length > 2 && (
                                              <Chip
                                                label={`+${
                                                  binding.roleMappings.length -
                                                  2
                                                }`}
                                                size="small"
                                                variant="outlined"
                                                style={{
                                                  marginLeft: 6,
                                                  fontSize: '0.65rem',
                                                  height: 18,
                                                }}
                                              />
                                            )}
                                        </Typography>
                                      ))}
                                  </>
                                ) : (
                                  <Typography variant="body2">
                                    {'\u2014'}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell className={classes.entitlementCell}>
                                <Typography variant="body2">
                                  {binding.entitlement.claim}=
                                  {binding.entitlement.value}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={binding.effect.toUpperCase()}
                                  size="small"
                                  variant="outlined"
                                  className={`${classes.effectChip} ${
                                    binding.effect === 'allow'
                                      ? classes.allowChip
                                      : classes.denyChip
                                  }`}
                                />
                              </TableCell>
                              <TableCell
                                align="right"
                                onClick={e => e.stopPropagation()}
                              >
                                <Tooltip title={updateDeniedTooltip || 'Edit'}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditBinding(binding)}
                                      disabled={!canUpdate}
                                      color="primary"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip
                                  title={deleteDeniedTooltip || 'Delete'}
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleDeleteBinding(binding)
                                      }
                                      disabled={!canDelete}
                                      className={classes.deleteIconButton}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
        </>
      )}

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle disableTypography>
          <Typography variant="h4">Delete Namespace Role Binding</Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {bindingToDelete && (
              <>
                Are you sure you want to delete the namespace role binding
                &nbsp;
                <strong>{bindingToDelete.name}</strong>?
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteBinding}
            variant="outlined"
            className={classes.deleteButton}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <BindingDetailDialog
        open={!!detailBinding}
        onClose={() => setDetailBinding(null)}
        onEdit={
          canUpdate && detailBinding
            ? () => {
                const original = bindings.find(
                  b => b.name === detailBinding.name,
                );
                setDetailBinding(null);
                if (original) {
                  handleEditBinding(original);
                }
              }
            : undefined
        }
        binding={detailBinding}
      />
    </Box>
  );
};
